"use client";

import { Puzzle } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import {
	detectExtensionBrowserSupport,
	EXTENSION_STORE_URL,
} from "@/lib/extension/browser";
import { isPromptSnoozed, snoozePrompt } from "@/lib/extension/snooze";
import { useExtensionInstalled } from "@/lib/extension/use-extension-installed";

// Holds a snooze-until timestamp; "not now" hides it for 14 days, not forever.
const DISMISS_KEY = "ld-apply-install-nudge-dismissed";

// Tags shown/clicked events so before/after copy can be compared in PostHog.
const COPY_VERSION = "benchmark-2026-10";

// ATS buckets measured in scripts/research/apply-benchmark-2026-10: about
// 2 of 3 required fields filled (64-76%). Other supported ATSs were not
// benchmarked, so they get copy without the number.
const BENCHMARKED_ATS = new Set(["greenhouse", "lever", "ashby"]);

/**
 * Contextual install CTA shown at the highest-intent moment: a visitor (guest
 * or signed in) on desktop Chrome, Edge or Brave, about to apply to a job whose ATS the extension can autofill, when they don't
 * have the extension. Converts the "I'm about to fill this form by hand" moment
 * into an install. Additive — never blocks the Apply button. Dismissible.
 */
export function ExtensionInstallNudge({
	locale,
	jobId,
	atsName,
}: {
	locale: string;
	jobId: string;
	/** The supported-ATS bucket (e.g. "greenhouse") for telemetry only. */
	atsName: string;
}) {
	const posthog = usePostHog();
	const installed = useExtensionInstalled();
	const [dismissed, setDismissed] = useState(true);
	const [installable, setInstallable] = useState(false);

	useEffect(() => {
		setInstallable(detectExtensionBrowserSupport() === "installable");
		setDismissed(isPromptSnoozed(DISMISS_KEY));
	}, []);

	const show = installable && installed === false && !dismissed;

	useEffect(() => {
		if (show) {
			posthog?.capture("extension_install_nudge_shown", {
				surface: "job_apply_cta",
				ats: atsName,
				job_id: jobId,
				locale,
				copy_version: COPY_VERSION,
			});
		}
	}, [show, posthog, atsName, jobId, locale]);

	if (!show) return null;
	const isEn = locale === "en";
	const benchmarked = BENCHMARKED_ATS.has(atsName);

	return (
		<div className="mt-2 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs">
			<Puzzle
				className="mt-0.5 h-4 w-4 shrink-0 text-primary"
				aria-hidden="true"
			/>
			<div className="flex flex-col gap-0.5">
				<span className="font-medium text-foreground">
					{benchmarked
						? isEn
							? "Our free extension fills about 2 of 3 required fields. You check, then send it yourself."
							: "Ekstensi gratis kami mengisi sekitar 2 dari 3 kolom wajib. Kamu cek, lalu kirim sendiri."
						: isEn
							? "Our free extension helps fill this form. You check, then send it yourself."
							: "Ekstensi gratis kami membantu mengisi formulir ini. Kamu cek, lalu kirim sendiri."}
				</span>
				<span className="text-muted-foreground">
					<a
						href={EXTENSION_STORE_URL}
						target="_blank"
						rel="noopener noreferrer"
						onClick={() =>
							posthog?.capture("extension_install_nudge_clicked", {
								surface: "job_apply_cta",
								ats: atsName,
								job_id: jobId,
								locale,
								copy_version: COPY_VERSION,
							})
						}
						className="font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					>
						{isEn ? "Add the free extension" : "Pasang ekstensi gratis"} →
					</a>{" "}
					·{" "}
					<button
						type="button"
						onClick={() => {
							snoozePrompt(DISMISS_KEY);
							setDismissed(true);
							posthog?.capture("extension_install_nudge_dismissed", {
								surface: "job_apply_cta",
								ats: atsName,
								job_id: jobId,
								locale,
							});
						}}
						className="underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					>
						{isEn ? "not now" : "nanti saja"}
					</button>
				</span>
			</div>
		</div>
	);
}
